import { useEffect, useId, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/Button/Button';
import { RevealableInput } from '../components/RevealableInput/RevealableInput';
import { AppFooter } from '../branding/AppFooter';
import { AppHeader } from '../branding/AppHeader';
import { useConsumePasswordReset } from './useAuth';
import './AuthDialog.css';

/**
 * `/reset-password?token=<token>` — landing page from a password-reset
 * email. Asks for the new password, hands the token + new password to
 * the backend, and lands the kid signed in on success.
 *
 * Reuses the `bl-auth__*` styles so the page reads as a sibling of
 * the AuthDialog rather than a one-off form.
 */
export function ResetPasswordPage(): JSX.Element {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const newId = useId();
  const confirmId = useId();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const consume = useConsumePasswordReset();

  useEffect(() => {
    if (consume.isSuccess) {
      // Land the kid in the studio. They're already signed in (the
      // server set the cookie + we updated the auth cache), so this
      // is just a navigation, not another auth round-trip.
      const t = window.setTimeout(() => navigate('/studio', { replace: true }), 800);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [consume.isSuccess, navigate]);

  const newValid = newPassword.length >= 8;
  const confirmMatches = confirmPassword === newPassword && confirmPassword.length > 0;
  const canSubmit = !!token && newValid && confirmMatches && !consume.isPending;

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSubmit) return;
    consume.mutate({ token, newPassword });
  };

  if (!token) {
    return (
      <PageShell>
        <h1 className="bl-auth__title">Hmm.</h1>
        <p className="bl-auth__error" role="alert">
          This reset link is missing its token. Ask for a fresh one from the sign-in screen.
        </p>
        <p>
          <Link to="/">← Back home</Link>
        </p>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <h1 className="bl-auth__title">Pick a new password</h1>
      {consume.isSuccess ? (
        <p className="bl-auth__sent" role="status">
          Your password is updated. Taking you to the studio…
        </p>
      ) : (
        <form className="bl-auth__form" onSubmit={handleSubmit}>
          <div className="bl-auth__field">
            <label htmlFor={newId} className="bl-auth__label">
              New password
              <span className="bl-auth__hint"> (8 or more characters)</span>
            </label>
            <RevealableInput
              id={newId}
              autoComplete="new-password"
              required
              minLength={8}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              data-autofocus
            />
          </div>

          <div className="bl-auth__field">
            <label htmlFor={confirmId} className="bl-auth__label">
              Type it again to be sure
            </label>
            <RevealableInput
              id={confirmId}
              autoComplete="new-password"
              required
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              aria-invalid={
                confirmPassword.length > 0 && !confirmMatches ? true : undefined
              }
              aria-describedby={
                confirmPassword.length > 0 && !confirmMatches ? 'bl-reset-mismatch' : undefined
              }
            />
            {confirmPassword.length > 0 && !confirmMatches && (
              <p
                id="bl-reset-mismatch"
                className="bl-auth__inline-notice bl-auth__inline-notice--warn"
              >
                Those don&apos;t match yet.
              </p>
            )}
          </div>

          <div className="bl-auth__actions">
            <Button type="submit" disabled={!canSubmit}>
              {consume.isPending ? 'Working…' : 'Set new password'}
            </Button>
          </div>

          {consume.error && (
            <p role="alert" className="bl-auth__error">
              {consume.error.message}
            </p>
          )}
        </form>
      )}
    </PageShell>
  );
}

function PageShell({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <>
      <AppHeader />
      <main className="bl-auth" style={{ margin: '2rem auto' }}>
        {children}
      </main>
      <AppFooter />
    </>
  );
}
